import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Trash2, Plus, Crosshair, ScanSearch, Link2, ListOrdered, Eye, EyeOff } from 'lucide-react';
import { getInternalRefTypes } from './RichParaEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const uid = () => Math.random().toString(36).substring(2, 10);

// Parse a <hotspot> DOM element back into the UI model (shared by round-trip parsers)
export const parseHotspotEl = (h) => {
  const hs = {
    id: h.getAttribute('id') || uid(),
    title: h.getAttribute('hotspotTitle') || '',
    coords: h.getAttribute('objectCoordinates') || '',
    apsIdent: h.getAttribute('applicationStructureIdent') || undefined,
    apsName: h.getAttribute('applicationStructureName') || undefined,
    hsType: h.getAttribute('hotspotType') || undefined,
    descr: h.getAttribute('objectDescr') || undefined,
  };
  const ir = h.querySelector(':scope > internalRef');
  const dmc = h.querySelector(':scope > dmRef dmCode');
  if (ir) {
    hs.link = { type: 'internalRef', refId: ir.getAttribute('internalRefId') || '', targetType: ir.getAttribute('internalRefTargetType') || '' };
  } else if (dmc) {
    const a = (n) => dmc.getAttribute(n) || '';
    hs.link = {
      type: 'dmRef',
      dmc: `DMC-${a('modelIdentCode')}-${a('systemDiffCode')}-${a('systemCode')}-${a('subSystemCode')}${a('subSubSystemCode')}-${a('assyCode')}-${a('disassyCode')}${a('disassyCodeVariant')}-${a('infoCode')}${a('infoCodeVariant')}-${a('itemLocationCode')}`,
    };
  }
  return hs;
};

// SVG schematic of CGM callout regions (VDC coords, y-flipped for screen)
const HotspotPreview = ({ hotspots, dirIcns, icn }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const regions = hotspots.filter(h => Array.isArray(h.region) && h.region.length >= 4);

  useEffect(() => {
    let url = null;
    const raster = icn ? dirIcns.find(x => x.base === icn && /\.(png|jpe?g|gif|webp|svg)$/i.test(x.name) && x.handle) : null;
    if (raster) {
      raster.handle.getFile().then(f => { url = URL.createObjectURL(f); setImgUrl(url); }).catch(() => setImgUrl(null));
    } else setImgUrl(null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [icn, dirIcns]);

  if (regions.length === 0) {
    return <p className="text-[10px] text-slate-400 italic ml-2.5">No region data on these hotspots — use "Read hotspots from CGM" to load callout regions.</p>;
  }

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  regions.forEach(h => {
    for (let k = 0; k + 1 < h.region.length; k += 2) {
      minX = Math.min(minX, h.region[k]); maxX = Math.max(maxX, h.region[k]);
      minY = Math.min(minY, h.region[k + 1]); maxY = Math.max(maxY, h.region[k + 1]);
    }
  });
  const pad = Math.max(maxX - minX, maxY - minY) * 0.08 || 10;
  const W = (maxX - minX) + pad * 2;
  const H = (maxY - minY) + pad * 2;
  const tx = (x) => (x - minX) + pad;
  const ty = (y) => (maxY - y) + pad;
  const fs = Math.max(W, H) * 0.028;
  const hovered = hoverId ? hotspots.find(h => h.id === hoverId) : null;

  return (
    <div className="ml-2.5 space-y-1" data-testid="hotspot-preview">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-h-[380px] bg-slate-50 border border-slate-200 rounded" data-testid="hotspot-preview-svg">
        {imgUrl && <image href={imgUrl} x="0" y="0" width={W} height={H} opacity="0.45" preserveAspectRatio="xMidYMid meet" />}
        {regions.map(h => {
          const pts = [];
          let cx = 0, cy = 0, np = 0;
          for (let k = 0; k + 1 < h.region.length; k += 2) {
            const px = tx(h.region[k]), py = ty(h.region[k + 1]);
            pts.push(`${px},${py}`);
            cx += px; cy += py; np += 1;
          }
          cx /= np; cy /= np;
          const active = hoverId === h.id;
          return (
            <g key={h.id} onMouseEnter={() => setHoverId(h.id)} onMouseLeave={() => setHoverId(null)} style={{ cursor: 'pointer' }}>
              <polygon
                points={pts.join(' ')}
                fill={active ? 'rgba(8,145,178,0.4)' : 'rgba(8,145,178,0.15)'}
                stroke="#0891b2"
                strokeWidth={Math.max(W, H) * 0.0025}
                data-testid={`hotspot-region-${h.id}`}
              />
              <text x={cx} y={cy + fs * 0.35} fontSize={fs} fill="#0e7490" textAnchor="middle" fontWeight="600" style={{ pointerEvents: 'none' }}>
                {h.apsName || ''}
              </text>
              <title>{`${h.apsIdent || h.id}${h.title ? ` — ${h.title}` : ''}`}</title>
            </g>
          );
        })}
      </svg>
      <p className="text-[10px] text-slate-500 h-4" data-testid="hotspot-preview-hover-label">
        {hovered ? `${hovered.apsIdent || hovered.id}${hovered.title ? ` — ${hovered.title}` : ' (no title yet)'}` : `${regions.length} callout region(s) from CGM — hover a region to identify it`}
      </p>
    </div>
  );
};

export const HotspotEditor = ({ element, onUpdate, dirIcns = [], internalTargets = [], irttOptions = [], dmlEntries = [], dirFiles = [] }) => {
  const [reading, setReading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const hotspots = element.hotspots || [];
  const hasRegions = hotspots.some(h => Array.isArray(h.region) && h.region.length >= 4);
  const irtts = irttOptions.length > 0 ? irttOptions : getInternalRefTypes();
  const cgmEntry = element.icn
    ? dirIcns.find(x => x.base === element.icn && /\.cgm$/i.test(x.name) && x.handle)
    : null;

  const dmOptions = useMemo(() => {
    const seen = new Set(); const opts = [];
    for (const e of dmlEntries) {
      const d = e.dmc_formatted;
      if (d && !seen.has(d.toUpperCase())) { seen.add(d.toUpperCase()); opts.push({ value: d, label: e.title || '' }); }
    }
    for (const f of dirFiles) {
      const nm = f.name || '';
      if (/^DMC-/i.test(nm) && /\.xml$/i.test(nm)) {
        const d = nm.replace(/\.xml$/i, '');
        if (!seen.has(d.toUpperCase())) { seen.add(d.toUpperCase()); opts.push({ value: d, label: '' }); }
      }
    }
    return opts;
  }, [dmlEntries, dirFiles]);

  const addHotspot = () => {
    const base = element.id || 'fig';
    const existing = new Set(hotspots.map(h => h.id));
    let n = 0, id;
    do { n += 1; id = `${base}-hot-${String(n).padStart(4, '0')}`; } while (existing.has(id));
    onUpdate({ ...element, hotspots: [...hotspots, { id, title: '', coords: '' }] });
  };
  const updateHotspot = (i, h) => {
    const hs = [...hotspots];
    hs[i] = h;
    onUpdate({ ...element, hotspots: hs });
  };
  const removeHotspot = (i) => {
    const hs = hotspots.filter((_, x) => x !== i);
    onUpdate({ ...element, hotspots: hs.length > 0 ? hs : undefined });
  };
  const setLinkType = (i, t) => {
    const h = hotspots[i];
    if (t === 'none') { const { link, ...rest } = h; updateHotspot(i, rest); return; }
    if (t === 'internalRef') updateHotspot(i, { ...h, link: { type: 'internalRef', refId: h.link?.refId || '', targetType: h.link?.targetType || '' } });
    else updateHotspot(i, { ...h, link: { type: 'dmRef', dmc: h.link?.dmc || '' } });
  };

  const readCgm = async () => {
    if (!cgmEntry) return;
    setReading(true);
    try {
      const file = await cgmEntry.handle.getFile();
      const fd = new FormData();
      fd.append('file', file, cgmEntry.name);
      const res = await axios.post(`${API}/author/parse-cgm-hotspots`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const parsed = res.data?.hotspots || [];
      if (parsed.length === 0) {
        toast.info(`No hotspot application structures found in ${cgmEntry.name}`);
        setReading(false);
        return;
      }
      const base = element.id || 'fig';
      const usedIds = new Set();
      const merged = parsed.map((p, idx) => {
        const existing = hotspots.find(h => h.apsIdent === p.apsIdent);
        const numMatch = (p.apsIdent || '').match(/(\d+)$/);
        let num = numMatch ? parseInt(numMatch[1], 10) : idx;
        let id = existing?.id || `${base}-hot-${String(num).padStart(4, '0')}`;
        while (usedIds.has(id)) { num += 1; id = `${base}-hot-${String(num).padStart(4, '0')}`; }
        usedIds.add(id);
        return {
          id,
          apsIdent: p.apsIdent,
          apsName: p.apsName,
          hsType: 'CALLOUT',
          title: existing?.title || '',
          coords: existing?.coords || '',
          ...(p.region ? { region: p.region, regionType: p.regionType } : {}),
          ...(existing?.link ? { link: existing.link } : {}),
        };
      });
      onUpdate({ ...element, hotspots: merged });
      toast.success(`${merged.length} hotspot(s) read from ${cgmEntry.name}`);
    } catch (e) {
      toast.error('Failed to read hotspots from CGM');
    }
    setReading(false);
  };

  const applyBulkTitles = () => {
    const map = {};
    bulkText.split(/\r?\n/).forEach(line => {
      const m = line.match(/^\s*(\d+)[\s.:\-–)]*\s+(.+?)\s*$/);
      if (m) map[String(parseInt(m[1], 10))] = m[2];
    });
    if (Object.keys(map).length === 0) { toast.info('No numbered lines found — use lines like "1 BRAKE SUB-SYSTEM"'); return; }
    let applied = 0;
    const hs = hotspots.map(h => {
      let key = null;
      if (h.apsName && /^\d+$/.test(h.apsName.trim())) key = String(parseInt(h.apsName, 10));
      else { const m2 = (h.id || '').match(/(\d+)$/); if (m2) key = String(parseInt(m2[1], 10)); }
      if (key !== null && map[key] !== undefined) { applied += 1; return { ...h, title: map[key] }; }
      return h;
    });
    if (applied === 0) { toast.info('No hotspot numbers matched the pasted list'); return; }
    onUpdate({ ...element, hotspots: hs });
    setShowBulk(false); setBulkText('');
    toast.success(`${applied} hotspot title(s) applied`);
  };

  const linkBadge = (h) => {
    if (!h.link) return null;
    const label = h.link.type === 'internalRef' ? (h.link.refId || 'internalRef') : ((h.link.dmc || 'dmRef').length > 28 ? `${h.link.dmc.slice(0, 28)}…` : h.link.dmc || 'dmRef');
    return (
      <Badge variant="outline" className="text-[9px] font-mono bg-violet-50 text-violet-600 border-violet-200 shrink-0 px-1" data-testid={`hotspot-link-badge-${h.id}`}>
        → {label}
      </Badge>
    );
  };

  return (
    <div className="space-y-1.5" data-testid={`hotspots-${element.id}`}>
      {hotspots.length > 0 && (
        <div className="space-y-1.5 border-l-2 border-cyan-200 pl-2.5">
          <span className="text-[10px] font-semibold text-cyan-600 uppercase tracking-wider">Hotspots</span>
          {hotspots.map((h, i) => (
            <div key={h.id} className="space-y-1">
              <div className="flex items-center gap-1.5 group/hs" data-testid={`hotspot-row-${h.id}`}>
                <Badge variant="outline" className="text-[9px] font-mono bg-cyan-50 text-cyan-700 border-cyan-200 shrink-0 px-1">{h.id}</Badge>
                <Input
                  value={h.apsIdent || ''}
                  onChange={e => updateHotspot(i, { ...h, apsIdent: e.target.value || undefined })}
                  placeholder="APS ident"
                  title="applicationStructureIdent"
                  className="text-[10px] h-7 w-[92px] font-mono shrink-0"
                  data-testid={`hotspot-aps-ident-${h.id}`}
                />
                <Input
                  value={h.apsName || ''}
                  onChange={e => updateHotspot(i, { ...h, apsName: e.target.value || undefined })}
                  placeholder="APS name"
                  title="applicationStructureName"
                  className="text-[10px] h-7 w-[80px] shrink-0"
                  data-testid={`hotspot-aps-name-${h.id}`}
                />
                {linkBadge(h)}
                <Input
                  value={h.title || ''}
                  onChange={e => updateHotspot(i, { ...h, title: e.target.value })}
                  placeholder="Hotspot title (e.g. BRAKE SUB-SYSTEM)..."
                  className="text-xs h-7 flex-1"
                  data-testid={`hotspot-title-${h.id}`}
                />
                <button
                  onClick={() => setLinkOpen(linkOpen === h.id ? null : h.id)}
                  className={`p-1 shrink-0 transition-colors ${h.link || linkOpen === h.id ? 'text-violet-500 hover:text-violet-700' : 'text-slate-300 hover:text-violet-500'}`}
                  title="Link hotspot (internalRef / dmRef)"
                  data-testid={`hotspot-link-btn-${h.id}`}
                ><Link2 className="w-3 h-3" /></button>
                <button
                  onClick={() => removeHotspot(i)}
                  className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover/hs:opacity-100 transition-opacity shrink-0"
                  title="Delete hotspot"
                  data-testid={`delete-hotspot-${h.id}`}
                ><Trash2 className="w-3 h-3" /></button>
              </div>

              {linkOpen === h.id && (
                <div className="ml-4 p-2 rounded border border-violet-100 bg-violet-50/60 space-y-1.5" data-testid={`hotspot-link-form-${h.id}`}>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-semibold text-slate-500 mr-1">Callout jumps to:</span>
                    {[['none', 'No link'], ['internalRef', 'internalRef'], ['dmRef', 'dmRef']].map(([t, lbl]) => (
                      <button
                        key={t}
                        onClick={() => setLinkType(i, t)}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-mono border transition-colors ${(h.link?.type || 'none') === t ? 'bg-violet-100 border-violet-300 text-violet-700' : 'bg-white border-slate-200 text-slate-500 hover:border-violet-200'}`}
                        data-testid={`hotspot-link-type-${t}-${h.id}`}
                      >{lbl}</button>
                    ))}
                  </div>
                  {h.link?.type === 'internalRef' && (
                    <div className="space-y-1.5">
                      {internalTargets.length > 0 && (
                        <Select onValueChange={(val) => {
                          const t = internalTargets[parseInt(val, 10)];
                          if (t) updateHotspot(i, { ...h, link: { type: 'internalRef', refId: t.id, targetType: t.targetType } });
                        }}>
                          <SelectTrigger className="text-xs h-7 bg-white" data-testid={`hotspot-link-int-select-${h.id}`}>
                            <SelectValue placeholder="Select target in this data module..." />
                          </SelectTrigger>
                          <SelectContent>
                            {internalTargets.map((t, ti) => (
                              <SelectItem key={ti} value={String(ti)}>
                                <span className="text-xs">{t.label}</span>
                                <span className="ml-2 font-mono text-[10px] text-slate-400">{t.id}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <div className="flex items-center gap-1.5">
                        <Input
                          value={h.link.refId || ''}
                          onChange={e => updateHotspot(i, { ...h, link: { ...h.link, refId: e.target.value } })}
                          placeholder="Target id (e.g. fig-0002)"
                          className="text-xs h-7 flex-1 font-mono"
                          data-testid={`hotspot-link-refid-${h.id}`}
                        />
                        <Select value={h.link.targetType || ''} onValueChange={(v) => updateHotspot(i, { ...h, link: { ...h.link, targetType: v } })}>
                          <SelectTrigger className="text-xs h-7 w-[170px] bg-white" data-testid={`hotspot-link-irtt-${h.id}`}>
                            <SelectValue placeholder="Target type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {irtts.map(o => (
                              <SelectItem key={o.value} value={o.value}>
                                <span className="font-mono text-[10px]">{o.value}</span>
                                <span className="ml-2 text-xs text-slate-500">{o.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  {h.link?.type === 'dmRef' && (
                    <div className="space-y-1.5">
                      {dmOptions.length > 0 && (
                        <Select onValueChange={(val) => updateHotspot(i, { ...h, link: { type: 'dmRef', dmc: val } })}>
                          <SelectTrigger className="text-xs h-7 bg-white" data-testid={`hotspot-link-dm-select-${h.id}`}>
                            <SelectValue placeholder="Select Data Module..." />
                          </SelectTrigger>
                          <SelectContent>
                            {dmOptions.map((o, oi) => (
                              <SelectItem key={oi} value={o.value}>
                                <span className="font-mono text-[10px]">{o.value}</span>
                                {o.label && <span className="ml-2 text-xs text-slate-500">{o.label}</span>}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Input
                        value={h.link.dmc || ''}
                        onChange={e => updateHotspot(i, { ...h, link: { ...h.link, dmc: e.target.value } })}
                        placeholder="DMC code (e.g. DMC-S1000DBIKE-AAA-D00-00-00-00AA-041A-A)"
                        className="text-xs h-7 font-mono"
                        data-testid={`hotspot-link-dmc-${h.id}`}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showBulk && (
        <div className="ml-2.5 p-2 rounded border border-cyan-100 bg-cyan-50/60 space-y-1.5" data-testid={`bulk-titles-form-${element.id}`}>
          <span className="text-[10px] font-semibold text-slate-500">Paste a numbered parts list — each line: number + title (matched to hotspot callout numbers)</span>
          <Textarea
            value={bulkText}
            onChange={e => setBulkText(e.target.value)}
            placeholder={'Paste your list here — one line per hotspot, e.g. "1 BRAKE SUB-SYSTEM"'}
            rows={4}
            className="text-xs font-mono resize-y bg-white"
            data-testid={`bulk-titles-input-${element.id}`}
          />
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-6 text-xs gap-1" onClick={applyBulkTitles} disabled={!bulkText.trim()} data-testid={`bulk-titles-apply-${element.id}`}>
              <Plus className="w-3 h-3" /> Apply Titles
            </Button>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { setShowBulk(false); setBulkText(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-cyan-600 hover:text-cyan-800" onClick={addHotspot} data-testid={`add-hotspot-${element.id}`}>
          <Crosshair className="w-3 h-3" /> Add Hotspot
        </Button>
        {cgmEntry && (
          <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-violet-600 hover:text-violet-800" onClick={readCgm} disabled={reading} data-testid={`read-cgm-hotspots-${element.id}`}>
            <ScanSearch className="w-3 h-3" /> {reading ? 'Reading CGM…' : 'Read hotspots from CGM'}
          </Button>
        )}
        {hotspots.length > 0 && !showBulk && (
          <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-slate-500 hover:text-slate-700" onClick={() => setShowBulk(true)} data-testid={`bulk-titles-btn-${element.id}`}>
            <ListOrdered className="w-3 h-3" /> Bulk Titles
          </Button>
        )}
        {hasRegions && (
          <Button variant="ghost" size="sm" className={`text-[10px] h-6 gap-1 ${showPreview ? 'text-cyan-700' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setShowPreview(!showPreview)} data-testid={`preview-hotspots-btn-${element.id}`}>
            {showPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {showPreview ? 'Hide Preview' : 'Preview Regions'}
          </Button>
        )}
      </div>
      {showPreview && hasRegions && (
        <div className="flex gap-2 items-start flex-wrap md:flex-nowrap">
          <div className="flex-1 min-w-[55%]">
            <HotspotPreview hotspots={hotspots} dirIcns={dirIcns} icn={element.icn} />
          </div>
          {element.definitionList?.items?.length > 0 && (
            <div className="w-full md:w-[40%] ml-2.5 md:ml-0 border border-emerald-200 rounded bg-emerald-50/40 p-2.5 space-y-1" data-testid={`legend-preview-${element.id}`}>
              <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Legend</span>
              <table className="w-full text-xs">
                <tbody>
                  {element.definitionList.items.map((item, i) => (
                    <tr key={i} className="align-top">
                      <td className="pr-2 py-0.5 font-semibold text-slate-700 whitespace-nowrap">{item.term || '—'}</td>
                      <td className="py-0.5 text-slate-600">
                        {(item.def || '').split('\n').filter(Boolean).map((ln, li) => <p key={li}>{ln}</p>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
